// Canonical home for the dynamic-placeholder catalog. Both the client
// (`client/src/lib/message-placeholders.ts`) and the server
// (`server/utils/message-placeholders.ts`) re-export from here so the token
// list, context type, and substitution logic stay in lockstep automatically.

export interface PlaceholderOption {
  label: string;
  token: string;
}

export const PLACEHOLDER_OPTIONS: readonly PlaceholderOption[] = [
  { label: "Member first name", token: "{{member_first_name}}" },
  { label: "Member full name", token: "{{member_full_name}}" },
  { label: "Property name", token: "{{property_name}}" },
  { label: "Full property address", token: "{{property_full_address}}" },
  { label: "Short property address", token: "{{property_short_address}}" },
  { label: "Room number", token: "{{room_number}}" },
  { label: "Today's date", token: "{{today_date}}" },
  { label: "Move-in date", token: "{{member_move_in_date}}" },
  { label: "Lease end date", token: "{{member_lease_end_date}}" },
  { label: "Weekly rent", token: "{{weekly_rent}}" },
  { label: "Host name", token: "{{host_name}}" },
] as const;

// Derive the short address by stripping the leading street number (the numeric
// prefix before the first space) from the full address. e.g.
// "123 Main St, Philadelphia, PA" -> "Main St, Philadelphia, PA".
export function deriveShortAddress(fullAddress: string): string {
  return fullAddress.replace(/^\s*\d+\s+/, "").trim();
}

// Format a date value (ISO string, Date object, or null/undefined) as
// "Month DD, YYYY" (e.g. "July 23, 2026"). Returns "" when the value is
// absent or unparseable.
//
// Date-only ISO strings ("YYYY-MM-DD") are parsed as local calendar dates
// rather than UTC midnight — `new Date("YYYY-MM-DD")` treats the string as UTC
// and shifts to the prior day in behind-UTC timezones (e.g. all US locales).
// Full datetime strings that include time/timezone info are passed through to
// the Date constructor as-is.
export function formatPlaceholderDate(val: string | Date | null | undefined): string {
  if (!val) return "";
  let d: Date;
  if (val instanceof Date) {
    d = val;
  } else {
    // Match bare date strings: YYYY-MM-DD (with optional trailing time part)
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
    if (dateOnly) {
      // Parse as local time to avoid UTC-midnight timezone shift.
      d = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    } else {
      d = new Date(val);
    }
  }
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

// Format a numeric dollar amount (string or number from the DB) as a dollar
// amount with two decimal places (e.g. "$175.00"). Returns "" when absent.
export function formatWeeklyRent(val: string | number | null | undefined): string {
  if (val == null || val === "") return "";
  const n = Number(val);
  if (isNaN(n)) return "";
  return `$${n.toFixed(2)}`;
}

export interface MessageVarContext {
  memberFirstName?: string | null;
  memberFullName?: string | null;
  propertyName?: string | null;
  // The full property address; the short address is derived from it.
  propertyFullAddress?: string | null;
  roomNumber?: string | null;
  // New tokens — co-living messaging
  memberMoveInDate?: string | Date | null;
  memberLeaseEndDate?: string | Date | null;
  weeklyRent?: string | number | null;
  hostName?: string | null;
}

export interface SubstituteOptions {
  /**
   * When true, tokens whose resolved value would be an empty string are left
   * as the raw `{{token}}` in the output instead of producing a blank gap.
   * Use this in playground/preview contexts so hosts can see exactly which
   * tokens are unresolvable; leave false (default) for the actual dispatch
   * path where the original substitution behaviour is preserved.
   */
  keepMissingTokens?: boolean;
}

export function substituteMessageVars(
  body: string,
  ctx: MessageVarContext,
  opts: SubstituteOptions = {},
): string {
  const keep = opts.keepMissingTokens ?? false;
  let out = body;
  if (ctx.memberFirstName) out = out.replace(/\{\{member_first_name\}\}/g, ctx.memberFirstName);
  if (ctx.memberFullName) out = out.replace(/\{\{member_full_name\}\}/g, ctx.memberFullName);
  if (ctx.propertyName) out = out.replace(/\{\{property_name\}\}/g, ctx.propertyName);
  if (ctx.propertyFullAddress) {
    out = out.replace(/\{\{property_full_address\}\}/g, ctx.propertyFullAddress);
    out = out.replace(/\{\{property_short_address\}\}/g, deriveShortAddress(ctx.propertyFullAddress));
  }
  if (ctx.roomNumber) out = out.replace(/\{\{room_number\}\}/g, ctx.roomNumber);

  // {{today_date}} is always resolved to the current date at substitution
  // time — no context field needed.
  if (out.includes("{{today_date}}")) {
    out = out.replace(/\{\{today_date\}\}/g, formatPlaceholderDate(new Date()));
  }

  if (out.includes("{{member_move_in_date}}")) {
    const resolved = formatPlaceholderDate(ctx.memberMoveInDate);
    if (resolved || !keep) out = out.replace(/\{\{member_move_in_date\}\}/g, resolved);
  }
  if (out.includes("{{member_lease_end_date}}")) {
    const resolved = formatPlaceholderDate(ctx.memberLeaseEndDate);
    if (resolved || !keep) out = out.replace(/\{\{member_lease_end_date\}\}/g, resolved);
  }
  if (out.includes("{{weekly_rent}}")) {
    const resolved = formatWeeklyRent(ctx.weeklyRent);
    if (resolved || !keep) out = out.replace(/\{\{weekly_rent\}\}/g, resolved);
  }
  if (out.includes("{{host_name}}")) {
    const resolved = ctx.hostName ?? "";
    if (resolved || !keep) out = out.replace(/\{\{host_name\}\}/g, resolved);
  }

  return out;
}

// Returns the list of token strings that appear in `body` but whose
// corresponding context value cannot be resolved to a non-empty string. These
// are the tokens that would either produce a blank gap in the message (for
// date/rent/host tokens) or remain visible as a raw {{token}} (for
// name/address/room tokens).
//
// {{today_date}} is always resolvable and is never included.
//
// Callers use this to:
//  - Surface an unresolved-token warning in the playground response so hosts
//    know which member fields need to be filled in.
//  - Force a rule dispatch to draft-for-review instead of auto-send when one
//    or more tokens in the message content cannot be resolved.
export function getMissingTokens(body: string, ctx: MessageVarContext): string[] {
  const missing: string[] = [];

  // Silent-blank tokens — replaced with "" when value is absent; the gap is
  // invisible to the reader which is the core problem this guards against.
  if (body.includes("{{member_move_in_date}}") && !formatPlaceholderDate(ctx.memberMoveInDate)) {
    missing.push("{{member_move_in_date}}");
  }
  if (body.includes("{{member_lease_end_date}}") && !formatPlaceholderDate(ctx.memberLeaseEndDate)) {
    missing.push("{{member_lease_end_date}}");
  }
  if (body.includes("{{weekly_rent}}") && !formatWeeklyRent(ctx.weeklyRent)) {
    missing.push("{{weekly_rent}}");
  }
  if (body.includes("{{host_name}}") && !ctx.hostName) {
    missing.push("{{host_name}}");
  }

  // Raw-token-visible tokens — left as {{token}} when value is absent.
  // Still reported so the host knows the member record is incomplete.
  if (body.includes("{{member_first_name}}") && !ctx.memberFirstName) {
    missing.push("{{member_first_name}}");
  }
  if (body.includes("{{member_full_name}}") && !ctx.memberFullName) {
    missing.push("{{member_full_name}}");
  }
  if (body.includes("{{property_name}}") && !ctx.propertyName) {
    missing.push("{{property_name}}");
  }
  if (body.includes("{{property_full_address}}") && !ctx.propertyFullAddress) {
    missing.push("{{property_full_address}}");
  }
  if (body.includes("{{property_short_address}}") && !ctx.propertyFullAddress) {
    missing.push("{{property_short_address}}");
  }
  if (body.includes("{{room_number}}") && !ctx.roomNumber) {
    missing.push("{{room_number}}");
  }

  return missing;
}

// Sample values used to render the authoring preview when no specific
// recipient is known. Every token resolves so the host never sees a stray
// {{token}} in the example.
export const SAMPLE_PLACEHOLDER_CONTEXT: Required<MessageVarContext> = {
  memberFirstName: "Jordan",
  memberFullName: "Jordan Lee",
  propertyName: "Maple House",
  propertyFullAddress: "123 Maple Ave, Philadelphia, PA 19104",
  roomNumber: "4",
  memberMoveInDate: "2026-03-01",
  memberLeaseEndDate: "2027-02-28",
  weeklyRent: "175.00",
  hostName: "Alex Johnson",
};

// True when the body contains at least one of the known placeholder tokens —
// used to decide whether the preview is worth showing.
export function messageHasPlaceholders(body: string): boolean {
  return PLACEHOLDER_OPTIONS.some(({ token }) => body.includes(token));
}
